import { cookieUtils } from '@/utils/cookies'
import { 
  usePreferredLanguages, 
  useDevicePixelRatio
} from '@vueuse/core'
import { unref } from 'vue'

interface DeviceInfo {
  device_id: string
  device_os: string
  device_name: string
}

/**
 * Generates or retrieves a unique device identifier
 */
function getDeviceId(): string {
  const DEVICE_ID_COOKIE = 'device_id'
  
  // Try to get existing device ID from cookies
  let deviceId = cookieUtils.getCookie(DEVICE_ID_COOKIE)
  
  if (!deviceId) {
    // Generate a new device ID using timestamp and random values
    const timestamp = Date.now().toString(36)
    const randomPart = Math.random().toString(36).substring(2, 15)
    deviceId = `${timestamp}-${randomPart}`
    
    // Store it in cookies for 365 days
    cookieUtils.setCookie(DEVICE_ID_COOKIE, deviceId, 365)
  }
  
  return deviceId
}

/**
 * Enhanced device OS detection using user agent analysis
 */
function getDeviceOS(): string {
  const userAgent = navigator.userAgent.toLowerCase()
  const platform = navigator.platform?.toLowerCase() || ''
  
  // Mobile platforms first
  if (/iphone|ipod/.test(userAgent)) return 'iOS'
  if (/ipad/.test(userAgent)) return 'iPadOS'
  if (/android/.test(userAgent)) return 'Android'
  
  // Desktop platforms
  if (/win/.test(platform) || /windows/.test(userAgent)) return 'Windows'
  if (/mac/.test(platform) || /macos|mac os x/.test(userAgent)) return 'macOS'
  if (/linux/.test(platform) || /linux/.test(userAgent)) return 'Linux'
  if (/cros/.test(userAgent)) return 'Chrome OS'
  
  return 'Unknown'
}

/**
 * Enhanced device/browser name detection with better mobile support
 */
function getDeviceName(): string {
  const userAgent = navigator.userAgent
  
  // Mobile devices with more specific detection
  if (/iPhone/.test(userAgent)) {
    // Extract iPhone model if possible
    const match = userAgent.match(/iPhone OS ([\d_]+)/)
    return match?.[1] ? `iPhone (iOS ${match[1].replace(/_/g, '.')})` : 'iPhone'
  }

  if (/iPad/.test(userAgent)) {
    const match = userAgent.match(/OS ([\d_]+)/)
    return match?.[1] ? `iPad (iOS ${match[1].replace(/_/g, '.')})` : 'iPad'
  }
  
  if (/Android/.test(userAgent)) {
    // Try to extract device model
    const modelMatch = userAgent.match(/;\s*([^;)]+)\s*\)/)
    const versionMatch = userAgent.match(/Android ([\d.]+)/)
    let deviceName = 'Android Device'

    if (modelMatch?.[1]) {
      deviceName = modelMatch[1].trim()
    }
    if (versionMatch?.[1]) {
      deviceName += ` (Android ${versionMatch[1]})`
    }
    return deviceName
  }
  
  // Desktop browsers with version info
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    const match = userAgent.match(/Chrome\/([\d.]+)/)
    return match ? `Chrome ${match[1]}` : 'Chrome Browser'
  }
  
  if (userAgent.includes('Firefox')) {
    const match = userAgent.match(/Firefox\/([\d.]+)/)
    return match ? `Firefox ${match[1]}` : 'Firefox Browser'
  }
  
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/([\d.]+)/)
    return match ? `Safari ${match[1]}` : 'Safari Browser'
  }
  
  if (userAgent.includes('Edg')) {
    const match = userAgent.match(/Edg\/([\d.]+)/)
    return match ? `Edge ${match[1]}` : 'Edge Browser'
  }
  
  if (userAgent.includes('Opera')) {
    const match = userAgent.match(/Opera\/([\d.]+)|OPR\/([\d.]+)/)
    return match ? `Opera ${match[1] || match[2]}` : 'Opera Browser'
  }
  
  return 'Web Browser'
}

/**
 * Collects comprehensive device information for logging using VueUse utilities
 */
export function collectDeviceInfo(): DeviceInfo {
  // Use VueUse composables for enhanced detection (where applicable)
  const { pixelRatio } = useDevicePixelRatio()
  const languages = usePreferredLanguages()
  
  // Get basic device info
  const basicInfo = {
    device_id: getDeviceId(),
    device_os: getDeviceOS(),
    device_name: getDeviceName()
  }
  
  // Enhance device name with additional context if available
  let enhancedDeviceName = basicInfo.device_name
  
  // Add pixel ratio for high-DPI displays
  if (unref(pixelRatio) > 1) {
    enhancedDeviceName += ` (${unref(pixelRatio)}x display)`
  }
  
  // Add primary language if available
  const primaryLanguage = unref(languages)[0]
  if (primaryLanguage && primaryLanguage !== 'en') {
    enhancedDeviceName += ` [${primaryLanguage}]`
  }
  
  return {
    ...basicInfo,
    device_name: enhancedDeviceName
  }
}